import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { WelcomePage } from './components/WelcomePage';
import { QuestionStep } from './components/QuestionStep';
import { ProgressBar } from './components/ProgressBar';
import { ResultsPage } from './components/ResultsPage';
import { LeadFormGated } from './components/LeadFormGated';
import { GAFAM_SERVICES, getScoreLevel } from './data/services';

import './App.css';

const QuestionnaireWidget = () => {
  const [currentView, setCurrentView] = useState('welcome'); // welcome, questionnaire, leadform, results
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedServices, setSelectedServices] = useState([]);
  const [score, setScore] = useState(0);
  const [recommendations, setRecommendations] = useState([]);
  const [leadData, setLeadData] = useState(null);

  const categories = GAFAM_SERVICES.categories;
  const totalSteps = categories.length;

  const handleStart = () => {
    setCurrentView('questionnaire');
    setCurrentStep(0);
  };

  const handleServiceToggle = useCallback((serviceId) => {
    const currentCategory = categories[currentStep];
    const isNoneOption = serviceId.startsWith('none_');
    
    setSelectedServices(prev => {
      // If clicking "none" option, deselect all services in this category
      if (isNoneOption) {
        const categoryServiceIds = currentCategory.services.map(s => s.id);
        const withoutCategoryServices = prev.filter(
          id => !categoryServiceIds.includes(id) && !id.startsWith(`none_${currentCategory.id}`)
        );
        
        if (prev.includes(serviceId)) {
          return withoutCategoryServices;
        }
        return [...withoutCategoryServices, serviceId];
      }
      
      // If clicking a regular service, remove "none" option for this category
      const noneOptionId = `none_${currentCategory.id}`;
      const withoutNone = prev.filter(id => id !== noneOptionId);
      
      if (prev.includes(serviceId)) {
        return withoutNone.filter(id => id !== serviceId);
      }
      return [...withoutNone, serviceId];
    });
  }, [currentStep, categories]);

  const calculateResults = useCallback(() => {
    let totalScore = 0;
    const recs = [];

    categories.forEach(category => {
      let categoryScore = 0;
      const selectedInCategory = [];

      category.services.forEach(service => {
        if (selectedServices.includes(service.id)) {
          totalScore += service.points;
          categoryScore += service.points;
          selectedInCategory.push(service.name);
        }
      });

      if (categoryScore > 0) {
        recs.push({
          category: category.label,
          selected_services: selectedInCategory,
          points: categoryScore,
          alternative: category.alternative
        });
      }
    });

    setScore(totalScore);
    setRecommendations(recs);
    return { totalScore, recs };
  }, [selectedServices, categories]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      calculateResults();
      setCurrentView('leadform'); // Show lead form before results
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleLeadSubmitted = (data) => {
    setLeadData(data);
    setCurrentView('results');
  };

  const handleRestart = () => {
    setSelectedServices([]);
    setScore(0);
    setRecommendations([]);
    setCurrentStep(0);
    setLeadData(null);
    setCurrentView('welcome');
  };

  const scoreLevel = getScoreLevel(score);

  return (
    <div className="widget-container min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <Card className="p-6 md:p-10 rounded-3xl shadow-lg border-0 bg-white/80 backdrop-blur">
          {currentView === 'welcome' && (
            <WelcomePage onStart={handleStart} />
          )}

          {currentView === 'questionnaire' && (
            <>
              <ProgressBar 
                currentStep={currentStep} 
                totalSteps={totalSteps} 
              />
              
              <QuestionStep
                category={categories[currentStep]}
                selectedServices={selectedServices}
                onServiceToggle={handleServiceToggle}
              />

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="rounded-full px-6"
                  data-testid="prev-btn"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Précédent
                </Button>
                
                <Button
                  onClick={handleNext}
                  className="rounded-full px-6 bg-[#0d2a47] hover:bg-[#1a4a7a] text-white"
                  data-testid="next-btn"
                >
                  {currentStep === totalSteps - 1 ? 'Voir mes résultats' : 'Suivant'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {currentView === 'leadform' && (
            <LeadFormGated
              score={score}
              level={scoreLevel.level}
              selectedServices={selectedServices}
              recommendations={recommendations}
              onSuccess={handleLeadSubmitted}
            />
          )}

          {currentView === 'results' && (
            <ResultsPage
              score={score}
              recommendations={recommendations}
              leadData={leadData}
              onRestart={handleRestart}
            />
          )}
        </Card>
      </div>
      
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<QuestionnaireWidget />} />
        <Route path="/embed" element={<QuestionnaireWidget />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
